import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Col, Row } from 'react-bootstrap';
import { useRouter } from 'next/router';
import { addProductReview } from 'aster-foundation/core/epharmacy/RatingsAndReviews/redux/actions';
import PropTypes from 'prop-types';
import Image from 'next/image';
import { IC_PROUDCT_NOT_BOUGHT, IMG_THANKS } from '../../../../public/images';
import CommonSpaceHolder from '@common/Component/CommonSpaceHolder/CommonSpaceHolder';
import { CheckboxControl, InputFieldControl } from '@common/Component/Forms';
import { errorHandlingEpharmacy, getAttributeItemFromArray, getReferralPageForGA } from '@common/Utility/common-utils';
import { ONLINE_PHARMACY_ROUTE, productReviewsErrorMessage } from '@common/Utility/constants';
import logger from '@common/Utility/logger';
import { useNavigationURL } from '@common/Utility/generateNavigationURL';

const RightSideBar = dynamic(() => import('../../../Common/Component/RightSideBar/RightSideBar'));
const StarRating = dynamic(() => import('../../../Common/Component/StarRating/StarRating'));

const AddProductReviews = ({ detailsData, setShowRating, userData, editReviewData }) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const generateURL = useNavigationURL();

  const { ratingReview, comments, anonymous, id } =
    editReviewData && editReviewData.length > 0 ? editReviewData[0] : {};
  const editReviewObject = {
    title: comments?.title,
    description: comments?.description,
    starRating: ratingReview?.ratingValue || 0,
    anonymous,
  };
  const [submitReview, setSubmitReview] = useState(false);
  const [reviewObject, setReviewObject] = useState(editReviewObject);
  const [isProductBought, setIsProductBought] = useState(true);
  const [productNotBoughtText, setProductNotBoughtText] = useState({});
  const [errorMessage, setErrorMessage] = useState({});
  const pageType = getReferralPageForGA(router?.asPath);
  const screenName = router?.pathname;

  const onAddNewReview = (name, value) => {
    setReviewObject((prev) => ({ ...prev, [name]: value }));
    if (value !== '') {
      setErrorMessage((prev) => ({ ...prev, [name]: null }));
    }
  };

  // Check Validation for Review Form
  const formValidation = () => {
    const { title, description, starRating } = reviewObject;
    const errorObj = {};
    if (!title || title === '') {
      errorObj.title = productReviewsErrorMessage.title;
    }
    if (!description || description === '') {
      errorObj.description = productReviewsErrorMessage.description;
    }
    if (starRating === 0) {
      errorObj.starRating = 'Please enter your experience';
    }
    return errorObj;
  };

  const onSubmitReview = () => {
    const errorObj = formValidation();
    if (Object.keys(errorObj).length === 0) {
      const { name, productId, sku } = detailsData || {};
      const body = {
        name,
        entity: 'product',
        productId,
        anonymous: reviewObject?.anonymous,
        skuId: sku,
        userPatientId: userData?.id,
        reviews: {
          ratingReview: {
            ratingValue: reviewObject?.starRating,
            bestRating: '5',
          },
          comments: {
            title: reviewObject?.title,
            description: reviewObject?.description,
          },
          author: {
            name: `${userData?.name?.[0]?.text}`,
            emailId: userData?.telecom?.find((item) => item?.system === 'email')?.value,
            nickname: `${userData?.name?.[0]?.text}`,
          },
        },
      };
      if (id) {
        body.id = id;
      }
      if (submitReview || !isProductBought) {
        setShowRating(false);
        setSubmitReview(false);
        router?.push({ pathname: generateURL(`${ONLINE_PHARMACY_ROUTE}`) });
      } else {
        dispatch(
          addProductReview({
            params: body,
            onSuccess: (response) => {
              if (response?.status === 200) {
                setErrorMessage({});
                if (response?.bought === false) {
                  setIsProductBought(false);
                  setProductNotBoughtText({
                    headOneText: response?.headOneText,
                    headTwoText: response?.headTwoText,
                  });
                } else {
                  setSubmitReview(true);
                }
              }
            },
            onFailure: (error) => {
              errorHandlingEpharmacy(error, true, screenName, pageType);
              logger.error('Add Product Review API Call Error', error);
            },
          }),
        );
      }
    } else {
      setErrorMessage(errorObj);
    }
  };

  useEffect(() => {
    if (editReviewData?.length > 0) {
      setReviewObject(editReviewObject);
    }
  }, [editReviewData?.length]);

  return (
    <RightSideBar
      modalTitle="Product Review"
      size="md"
      footerAlignment="center"
      primaryButtonText={submitReview || !isProductBought ? 'Continue Shopping' : 'Submit Review'}
      footerBtnFullWidth
      closeButton
      customClass="product-review-modal"
      handleClose={() => setShowRating(false)}
      primaryButtonClicked={() => onSubmitReview()}
      appVariant="epharmacy"
    >
      <div className="product-rating-wrapper">
        {!isProductBought && (
          <CommonSpaceHolder
            outerCustomClass="spaceHolder"
            icon={IC_PROUDCT_NOT_BOUGHT}
            title={productNotBoughtText?.headOneText}
            subtitle={productNotBoughtText?.headTwoText}
          />
        )}
        {submitReview ? (
          <CommonSpaceHolder
            outerCustomClass="spaceHolder"
            icon={IMG_THANKS}
            title="Thank you for sharing your experience."
            subtitle="Your review is submitted and will be published, once approved."
          />
        ) : (
          isProductBought && (
            <Row>
              <Col lg={12}>
                <div className="productInfo">
                  <div className="productPic">
                    {detailsData && (
                      <Image
                        src={getAttributeItemFromArray(detailsData?.customAttribute, 'small_image')}
                        width="70px"
                        height="70px"
                        alt={detailsData?.name}
                      />
                    )}
                  </div>
                  <div className="productName">{detailsData?.name}</div>
                </div>
              </Col>
              <Col lg={12}>
                <label className="form-label">Rate The Product</label>
                <StarRating
                  customClass="starRating"
                  rating={reviewObject?.starRating}
                  handleChange={(value) => {
                    onAddNewReview('starRating', value);
                    setErrorMessage({ ...errorMessage, starRating: null });
                  }}
                  validation={errorMessage?.starRating}
                />
              </Col>
              <Col lg={12} className="mb-3">
                <InputFieldControl
                  label="Review Title"
                  type="text"
                  placeholder="Review Title"
                  name="title"
                  isLabel
                  appVariant="epharmacy"
                  valueType={reviewObject?.title}
                  errorMessage={errorMessage?.title}
                  isInvalid={errorMessage?.title}
                  onChange={(e) => {
                    onAddNewReview(e.target.name, e.target.value);
                  }}
                />
              </Col>
              <Col lg={12} className="mb-3">
                <InputFieldControl
                  label="Write Your Review"
                  as="textarea"
                  placeholder="Write Your Review"
                  isTextArea
                  name="description"
                  rows="5"
                  isLabel
                  valueType={reviewObject?.description}
                  errorMessage={errorMessage?.description}
                  isInvalid={errorMessage?.description}
                  onChange={(e) => {
                    onAddNewReview(e.target.name, e.target.value);
                  }}
                />
              </Col>
              <Col lg={12}>
                <CheckboxControl
                  key="Test"
                  id="facilityName"
                  label="Keep My Review Anonymous"
                  name="facilityName"
                  className="epharmacy"
                  variant="epharmacy"
                  checked={reviewObject?.anonymous}
                  onClick={(e) => {
                    onAddNewReview('anonymous', e.target.checked);
                  }}
                />
              </Col>
            </Row>
          )
        )}
      </div>
    </RightSideBar>
  );
};

AddProductReviews.propTypes = {
  detailsData: PropTypes.object,
  setShowRating: PropTypes.func,
  userData: PropTypes.object,
  editReviewData: PropTypes.array,
};

AddProductReviews.defaultProps = {
  detailsData: {},
  setShowRating: () => {},
  userData: {},
  editReviewData: {},
};

export default AddProductReviews;
